import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, InputAdornment } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { useDebounce } from 'use-lodash-debounce';
import { NotFoundItems } from '../../../../components/NotFoundItems';
import { CustomPagination } from '../../../../components/CustomPagination';
import { Tab } from '../../../../components/Tab';
import { CircularProgressCenter } from '../../../../components/CircularProgressCenter';
import { SortApplication } from '../../components/SortApplication';
import { Application } from '../../components/Application';
import { CustomInput } from '../../../../components/CustomInput';
import { CustomMultiSelect } from '../../../../components/CustomMultiSelect';
import { ReactComponent as SearchIcon } from '../../../../assets/images/search.svg';
import { getApplicationsByManagerId } from '../../../../store/support/actions';
import {
  APPLICATION_STATUS_OPTIONS,
  APPLICATION_SORT_OPTIONS,
  DISABLED_CONTROL_TOOLTIP, PERMISSIONS
} from '../../../../constants/variables';
import { useWebSocket } from '../../../../hooks/useWebSocket';
import { visibleBySectionPermission } from '../../../../utils/visibleBySectionPermission';
import { setUsersPermissions } from '../../../../utils/setUsersPermissions';

const MY = 'MY';
const FOR_EXECUTION = 'FOR_EXECUTION';

export const MyApplications = () => {
  const [typeApplications, setTypeApplications] = useState(MY);
  const { data: wss } = useWebSocket();
  const dispatch = useDispatch();

  const { applications, applicationsFetching } = useSelector((state) => state.support);
  const { userData } = useSelector((state) => state.currentUser);
  const { userPermissions, currentUsersPermissions } = useSelector((state) => state.userPermissions);
  const permissions = setUsersPermissions(userPermissions, currentUsersPermissions?.permissions);

  const [filter, setFilter] = useState({
    order_by: APPLICATION_SORT_OPTIONS[0],
    order_type: 'desc',
    request_status: [...APPLICATION_STATUS_OPTIONS],
    page: 1,
    size: 8,
    search: '',
  });

  const debouncedValue = useDebounce(filter.search, 800);

  const isControlVisible = (sectionSlug) => visibleBySectionPermission(permissions, sectionSlug);

  //const isDisabledCreateCustomerPermission = !isControlVisible(PERMISSIONS.SUPPORT.CreateSupportBonusRequests.value);
  const isDisabledSearchCustomerPermission = !isControlVisible(PERMISSIONS.SUPPORT.SearchSupportBonusRequests.value);
  const isDisabledFilterCustomerPermission = !isControlVisible(PERMISSIONS.SUPPORT.FilterSupportBonusRequests.value);
  const isDisabledListCustomerPermission = !isControlVisible(PERMISSIONS.SUPPORT.ListSupportBonusRequestsByManager.value);
  const isDisabledListCustomerAccountPermission = !isControlVisible(PERMISSIONS.SUPPORT.RetrieveSupportBonusRequests.value);

  const isPageVisible = visibleBySectionPermission(
    permissions,
    [
      PERMISSIONS.SUPPORT.CreateSupportBonusRequests.value,
      PERMISSIONS.SUPPORT.ListSupportBonusRequestsByManager.value
    ],
    'or'
  );

  function getFullName(manager) {
    if (!manager) return '';

    const { firstName, lastName, fullname, middleName } = manager;

    if (fullname) return fullname;

    return (firstName && lastName) ? `${firstName} ${lastName}`.trim() : firstName || lastName || middleName || '';
  }

  useEffect(() => {
    dispatch(
      getApplicationsByManagerId({
        ...filter,
        order_by: filter.order_by?.value,
        request_status: filter.request_status?.map((status) => status.value).filter(status => status !== 'all'),
      }, userData.id)
    );  // eslint-disable-next-line
  }, [
    debouncedValue,
    filter.page,
    filter.order_by,
    filter.request_status,
    filter.order_type,
    wss,
  ]);

  const setTypeAllUseCallback = useCallback(() => {
    setTypeApplications(MY);
    setFilter({
      ...filter,
      request_status: [...APPLICATION_STATUS_OPTIONS],
      manager_id: userData.id,
      page: 1
    }); // eslint-disable-next-line
  }, [typeApplications, filter]);

  const setTypeForExecutionUseCallbacks = useCallback(() => {
    setTypeApplications(FOR_EXECUTION);
    delete filter.manager_id;
    setFilter({
      ...filter,
      request_status: [APPLICATION_STATUS_OPTIONS[1], APPLICATION_STATUS_OPTIONS[4]],
      page: 1
    }); // eslint-disable-next-line
  }, [typeApplications, filter]);

  if (!isPageVisible) {
    return <></>;
  }

  return (
    <Box>
      <Typography mb="24px" variant="h1" sx={{ color: 'text.primary' }} component="h1">
        Мои заявки
      </Typography>
      <Box display="flex" justifyContent="right" mb="24px">
        {/*<CustomButton
          component={Link}
          to={ROUTES.SUPPORT_CREATE_APPLICATION}
          size="small"
          variant="contained"
          disabled={isDisabledCreateCustomerPermission}
          tooltip={isDisabledCreateCustomerPermission ? DISABLED_CONTROL_TOOLTIP : null}
        >
          Создать
        </CustomButton>*/}

        <Box display="flex" alignItems="center">
          <Box sx={{ width: 375, marginRight: '16px' }}>
            <CustomMultiSelect
              name="request_status"
              onChange={e => setFilter({ ...filter, request_status: e, page: 1 })}
              variant="outlined"
              value={filter.request_status}
              options={APPLICATION_STATUS_OPTIONS}
              sx={{ marginRight: '56px' }}
              width={375}
              size="small"
              startAdornmentText={
                <InputAdornment
                  position="start"
                  sx={{
                    marginRight: 0
                  }}
                >
                  <Typography component="span" variant="body1" color="text.primary">
                    Статус:
                  </Typography>
                </InputAdornment>
              }
              placeholder="Статус заявки"
              disabled={isDisabledFilterCustomerPermission}
              tooltip={isDisabledFilterCustomerPermission && DISABLED_CONTROL_TOOLTIP}
            />
          </Box>

          <CustomInput
            type="search"
            placeholder="Поиск"
            variant="outlined"
            value={filter.search}
            sx={{ width: 240 }}
            size="small"
            onChange={(e) => setFilter({ ...filter, search: e.target.value, page: 1 })}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
            disabled={isDisabledSearchCustomerPermission}
            tooltip={isDisabledSearchCustomerPermission && DISABLED_CONTROL_TOOLTIP}
          />
        </Box>
      </Box>

      <SortApplication
        filter={filter}
        setFilter={setFilter}
        total={applications.total}
        disabled={isDisabledFilterCustomerPermission}
        tooltip={isDisabledFilterCustomerPermission && DISABLED_CONTROL_TOOLTIP}
      />

      <Box sx={{ display: 'flex' }} data-testid='Tabs'>
        <Tab
          label="Мои"
          setTypeTable={setTypeAllUseCallback}
          typeCurrent={typeApplications}
          typeDefault={MY}
        />
        <Tab
          label="К исполнению"
          setTypeTable={setTypeForExecutionUseCallbacks}
          typeCurrent={typeApplications}
          typeDefault={FOR_EXECUTION}
        />
      </Box>

      {applicationsFetching ? (
        <CircularProgressCenter marginBottom="150px" data-testid="CircularProgressCenter"/>
      ) : (
        applications.items?.map((item) => {
          return (
            <Application
              key={item.request_num}
              numberApplication={item.request_num}
              nameCreator={getFullName(item?.manager)}
              status={item.request_status}
              dateCreate={item.created_at}
              SLA={item.sla}
              bonusesNumber={item.subscriber?.account_number}
              id={item.id}
              handlerStatus={typeApplications === FOR_EXECUTION}
              userPermissions={permissions}
              disabled={isDisabledListCustomerAccountPermission}
              data-testid="Application"
            />
          );
        })
      )}
      {applications?.items?.length !== 0 && (
        <Box display="flex" justifyContent="flex-end" marginTop="24px">
          <CustomPagination
            onChange={(event, newPage) => setFilter({ ...filter, page: newPage })}
            page={filter.page}
            count={Math.ceil(applications?.total / applications?.size) || 0}
            disabled={isDisabledListCustomerPermission}
            tooltip={isDisabledListCustomerPermission ? DISABLED_CONTROL_TOOLTIP : null}
            data-testid="Pagination"
          />
        </Box>
      )}

      {!applicationsFetching && applications.items < 1 && <NotFoundItems />}
    </Box>
  );
};
